import { useEffect, useRef } from 'react';
import { getConversationId } from './conversationId';
import { subscribePrivateMessageInbox, countUnreadPrivateMessages } from './inbox';
import {
  filterFriendsEligibleForMessaging,
  isMessagingBlockedWithPlayer,
} from './messagingPolicy';
import {
  loadReadCursors,
  persistReadCursors,
  clearReadCursorForFriend,
} from './readCursors';

/**
 * App-level inbox: unread counts, read cursors, new-message notifications.
 */
export function usePrivateChatInbox({
  enabled,
  currentUserId,
  friends,
  openFriendId,
  onUnreadChange,
  onNewMessage,
}) {
  const chatReadCursorRef = useRef({});
  const chatInboxHydratedRef = useRef(false);
  const notifiedMessageIdsRef = useRef(new Set());
  const onUnreadChangeRef = useRef(onUnreadChange);
  const onNewMessageRef = useRef(onNewMessage);
  onUnreadChangeRef.current = onUnreadChange;
  onNewMessageRef.current = onNewMessage;

  useEffect(() => {
    if (!enabled || !currentUserId || friends.length === 0) {
      onUnreadChangeRef.current?.({});
      return undefined;
    }

    chatReadCursorRef.current = loadReadCursors(currentUserId);
    chatInboxHydratedRef.current = false;
    notifiedMessageIdsRef.current = new Set();

    const messagingFriends = filterFriendsEligibleForMessaging(friends);

    return subscribePrivateMessageInbox(currentUserId, messagingFriends, ({ messagesByFriendId }) => {
      const unread = {};

      Object.entries(messagesByFriendId).forEach(([friendId, messages]) => {
        const convId = getConversationId(currentUserId, friendId);
        const last = messages?.length ? messages[messages.length - 1] : null;
        const friend = friends.find((f) => f.id === friendId);

        if (isMessagingBlockedWithPlayer(friendId, friends)) {
          unread[friendId] = 0;
          return;
        }

        if (openFriendId === friendId && last) {
          chatReadCursorRef.current[convId] = last.id;
          unread[friendId] = 0;
        } else {
          unread[friendId] = countUnreadPrivateMessages(
            messages,
            chatReadCursorRef.current[convId],
            currentUserId
          );
        }

        if (!chatInboxHydratedRef.current) {
          messages?.forEach((m) => notifiedMessageIdsRef.current.add(m.id));
          return;
        }

        messages?.forEach((m) => {
          if (m.fromMe || notifiedMessageIdsRef.current.has(m.id)) return;
          if (openFriendId === friendId) {
            notifiedMessageIdsRef.current.add(m.id);
            return;
          }
          notifiedMessageIdsRef.current.add(m.id);
          const preview = (m.text || '').trim().slice(0, 80);
          onNewMessageRef.current?.({
            friendId,
            friendName: m.fromUserName || friend?.name || 'Friend',
            preview,
          });
        });
      });

      if (!chatInboxHydratedRef.current) {
        chatInboxHydratedRef.current = true;
        persistReadCursors(currentUserId, chatReadCursorRef.current);
      } else if (openFriendId) {
        persistReadCursors(currentUserId, chatReadCursorRef.current);
      }

      onUnreadChangeRef.current?.(unread);
    });
  }, [enabled, currentUserId, friends, openFriendId]);

  const clearInboxStateForFriend = (friendId) => {
    onUnreadChangeRef.current?.((prev) => {
      const next = { ...prev };
      delete next[friendId];
      return next;
    });
    if (currentUserId) {
      chatReadCursorRef.current = clearReadCursorForFriend(
        chatReadCursorRef.current,
        currentUserId,
        friendId
      );
      persistReadCursors(currentUserId, chatReadCursorRef.current);
    }
  };

  return { clearInboxStateForFriend };
}
